import Box from "@mui/material/Box";
import type { ReactNode } from "react";

import { useBootIndicatorLook } from "@/hooks/useBootIndicatorLook";
import { BOOT_INDICATOR_FADE_IN, BOOT_INDICATOR_FADE_OUT } from "@/ui/bootIndicatorVisuals";

/** Width of the left rail, which the overlay leaves uncovered so it stays clickable. */
const RAIL_WIDTH_PX = 60;

/**
 * Dims the content area and swallows its clicks while a cold bootstrap runs, leaving the rail usable. Stays mounted
 * outside the blocking look, transparent and click-through, so it can fade out.
 */
export default function BootBlockingOverlay() {
  const blocking = useBootIndicatorLook() === "blocking";

  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: `${RAIL_WIDTH_PX}px`,
        zIndex: 2,
        bgcolor: "rgba(0,0,0,0.45)",
        cursor: "progress",
        opacity: blocking ? 1 : 0,
        pointerEvents: blocking ? "auto" : "none",
        transition: blocking ? BOOT_INDICATOR_FADE_IN : BOOT_INDICATOR_FADE_OUT,
      }}
    />
  );
}

/**
 * Holds the rail and content in their own stacking context, with the overlay above it. Without one, any element in
 * the content with a `zIndex` above the overlay's (pinned table headers, toolbar icons) paints over the dim and takes
 * clicks during a cold bootstrap. Render it directly inside a `position: relative` container: the overlay is absolutely
 * positioned against it, and would dim the whole viewport otherwise.
 */
export function BootBlockingScope({ children }: { children: ReactNode }) {
  return (
    <>
      <Box sx={{ display: "flex", flexGrow: 1, minWidth: 0, height: "100%", isolation: "isolate" }}>{children}</Box>
      <BootBlockingOverlay />
    </>
  );
}
